import {DetailsFieldsSection} from '@/components/ui/details-atoms/DetailsAtoms'
import {disabiltyProfileLetterEToQualifiers, disabiltyProfileLetterToTitle} from '../../utils'
import {useTranslation} from '@/translations/i18n'
import {EditDetailsSectionButtons} from '@/components/commons/edit-details-section-buttons/EditDetailsSectionButtons'
import {DisabilityProfileIcfCode, DisabilityProfileLetter, ProjectExtended} from '../../types'
import {Dispatch, Fragment, SetStateAction} from 'react'
import {useEditProjectDisabilityProfiles} from '../../queries/useEditProjectDisabilityProfiles'
import {useQueryClient} from '@tanstack/react-query'
import {QUERY_KEYS} from '@/queryClient'
import {DisabilityProfileLetterCardFormSchema} from '../../formSchemas'
import {Controller, useFieldArray, useForm, useWatch} from 'react-hook-form'
import {zodResolver} from '@hookform/resolvers/zod'
import {errorHandler, raise} from '@/utilities/helpers'
import {StyledGrid, StyledIcfCodeCell} from './style'
import {Text} from '@/components/ui/text/Text'
import {SelectIcfCodes} from '../select-icf-codes/SelectIcfCodes'
import {InputSelect} from '@/components/commons/input-select/InputSelect'
import {Button} from '@/components/ui/button/Button'
import {Trash01Icon} from '@/components/ui/icon/Icon'
import {useOutletContext} from 'react-router-dom'

export const DisabilityProfileLetterE = ({
    editingLetter,
    setEditingLetter,
    projectId,
    defaultValues
}: {
    editingLetter: DisabilityProfileLetter | null
    setEditingLetter: Dispatch<SetStateAction<DisabilityProfileLetter | null>>
    projectId: string
    defaultValues: DisabilityProfileLetterCardFormSchema['values']
}) => {
    const queryClient = useQueryClient()
    const {t} = useTranslation()
    const {isEditable} = useOutletContext<{isEditable: boolean}>()
    const {control, reset, handleSubmit, setValue} = useForm<DisabilityProfileLetterCardFormSchema>({
        resolver: zodResolver(DisabilityProfileLetterCardFormSchema),
        defaultValues: {
            values: defaultValues.concat(
                new Array(5 - defaultValues.length).fill(() => ({
                    icfCode: undefined,
                    qualifier1: undefined,
                    type: null
                }))
            )
        }
    })
    const {fields, remove, append} = useFieldArray({
        control,
        name: 'values'
    })
    const {mutate, isPending} = useEditProjectDisabilityProfiles({
        urlParams: {
            projectId
        },
        options: {
            onSuccess: (_, variables) => {
                queryClient.setQueriesData<ProjectExtended | undefined>(
                    {queryKey: [QUERY_KEYS.PROJECT, projectId]},
                    old => {
                        if (!old) {
                            return old
                        }

                        return {
                            ...old,
                            disabilityProfiles: old.disabilityProfiles
                                .filter(profile => profile.letter != 'e')
                                .concat(
                                    variables.profile.map(profile => ({
                                        letter: 'e',
                                        icfCode: {code: profile.icfCode},
                                        type: profile.type ?? null,
                                        qualifier1: profile.qualifier1,
                                        qualifier2: null
                                    }))
                                )
                        }
                    }
                )
                queryClient.invalidateQueries({queryKey: [QUERY_KEYS.PROJECT_VALIDATORS, projectId]})
                setEditingLetter(null)
                reset({
                    values: variables.profile
                        .map(profile => ({
                            icfCode: profile.icfCode,
                            qualifier1: profile.qualifier1.toString(),
                            type: profile.type ?? null
                        }))
                        .concat(
                            Array(5 - variables.profile.length).fill({
                                icfCode: undefined,
                                qualifier1: undefined,
                                type: null
                            })
                        )
                })
            },
            onError: errorHandler
        }
    })

    const onSubmit = handleSubmit((data: DisabilityProfileLetterCardFormSchema) => {
        mutate({
            letter: 'e',
            profile: data.values
                .filter(value => value.icfCode)
                .map(value => ({
                    icfCode: value.icfCode ?? raise('icfCode is required'),
                    qualifier1: Number(value.qualifier1),
                    type: value.type ?? null,
                    letter: 'e'
                }))
        })
    })

    const valuesWatch = useWatch({control, name: 'values'})

    return (
        <DetailsFieldsSection
            variant="neutral"
            title={t(disabiltyProfileLetterToTitle.e)}
            isDisabled={!!editingLetter && editingLetter != 'e'}
            headingEndSlot={
                isEditable && (
                    <EditDetailsSectionButtons
                        isDisabled={isPending}
                        isLoading={isPending}
                        isActive={editingLetter == 'e'}
                        onConfirm={onSubmit}
                        onCancel={() => {
                            setEditingLetter(null)
                            reset()
                        }}
                        onEdit={() => setEditingLetter('e')}
                    />
                )
            }
        >
            <StyledGrid>
                <Text palette="neutral" shade="500" size="textXs">
                    {t('projects:icf_code')}
                </Text>
                <Text palette="neutral" shade="500" size="textXs">
                    {t('projects:disability_profile_e_qualifier1_title')}
                </Text>
                <Text palette="neutral" shade="500" size="textXs">
                    {t('projects:qualifier')}
                </Text>
                <div />
                {fields.map((field, rowIndex) => (
                    <Fragment key={field.id}>
                        <StyledIcfCodeCell>
                            <Text size="textSm" weight="500">
                                {rowIndex + 1}.
                            </Text>
                            {editingLetter == 'e' ? (
                                <Controller
                                    control={control}
                                    name={`values.${rowIndex}.icfCode`}
                                    render={({field, fieldState}) => (
                                        <SelectIcfCodes
                                            exludedOptions={
                                                valuesWatch
                                                    .map(value => value.icfCode)
                                                    .filter(Boolean) as DisabilityProfileIcfCode[]
                                            }
                                            disabled={
                                                rowIndex >= 1 &&
                                                (!valuesWatch[rowIndex - 1]?.icfCode ||
                                                    !valuesWatch[rowIndex - 1]?.qualifier1 ||
                                                    !valuesWatch[rowIndex - 1]?.qualifier2)
                                            }
                                            letter={'e'}
                                            value={field.value}
                                            onChange={field.onChange}
                                            errorMessage={fieldState.error?.message}
                                        />
                                    )}
                                />
                            ) : (
                                <Text size="textSm" palette="neutral" shade="600">
                                    {field?.icfCode ?? '-'}
                                </Text>
                            )}
                        </StyledIcfCodeCell>
                        {editingLetter == 'e' ? (
                            <Controller
                                control={control}
                                name={`values.${rowIndex}.type`}
                                render={({field, fieldState}) => (
                                    <InputSelect
                                        disabled={!valuesWatch[rowIndex]?.icfCode}
                                        sameWidth={false}
                                        displayValue={() => {
                                            const label = disabiltyProfileLetterEToQualifiers.type.options.find(
                                                option => option.value.toString() == field.value
                                            )?.label
                                            return label ? t(label) : field.value
                                        }}
                                        optionsGroups={[
                                            {
                                                options: disabiltyProfileLetterEToQualifiers.type.options.map(
                                                    option =>
                                                        [option.value.toString(), t(option.label)] as [string, string]
                                                )
                                            }
                                        ]}
                                        value={field.value ?? ''}
                                        onChange={value => {
                                            field.onChange(value)
                                            setValue(`values.${rowIndex}.qualifier1`, '')
                                        }}
                                        errorMessage={fieldState.error?.message}
                                    />
                                )}
                            />
                        ) : (
                            <Text size="textSm" palette="neutral" shade="600">
                                {valuesWatch[rowIndex]?.type == 'barrier'
                                    ? t('projects:disability_profile_e_qualifier1_0')
                                    : valuesWatch[rowIndex]?.type == 'helper'
                                      ? t('projects:disability_profile_e_qualifier1_1')
                                      : '-'}
                            </Text>
                        )}
                        {editingLetter == 'e' ? (
                            <Controller
                                control={control}
                                name={`values.${rowIndex}.qualifier1`}
                                render={({field, fieldState}) => (
                                    <InputSelect
                                        disabled={!valuesWatch[rowIndex]?.type}
                                        sameWidth={false}
                                        displayValue={() => field.value}
                                        optionsGroups={[
                                            {
                                                label: disabiltyProfileLetterEToQualifiers.qualifier[
                                                    valuesWatch[rowIndex]?.type ?? 'barrier'
                                                ].label,
                                                options: disabiltyProfileLetterEToQualifiers.qualifier[
                                                    valuesWatch[rowIndex]?.type ?? 'barrier'
                                                ].options.map(
                                                    option =>
                                                        [
                                                            option.value.toString(),
                                                            `${option.value} - ${t(option.label)}`
                                                        ] as [string, string]
                                                )
                                            }
                                        ]}
                                        value={field.value}
                                        onChange={field.onChange}
                                        errorMessage={fieldState.error?.message}
                                    />
                                )}
                            />
                        ) : (
                            <Text size="textSm" palette="neutral" shade="600">
                                {valuesWatch[rowIndex]?.qualifier1 ?? '-'}
                            </Text>
                        )}
                        {editingLetter == 'e' && valuesWatch[rowIndex]?.icfCode ? (
                            <Button
                                variant="ghost"
                                shape="square"
                                onClick={() => {
                                    remove(rowIndex)
                                    append({
                                        icfCode: undefined,
                                        qualifier1: undefined,
                                        type: null
                                    })
                                }}
                            >
                                <Trash01Icon />
                            </Button>
                        ) : (
                            <div />
                        )}
                    </Fragment>
                ))}
            </StyledGrid>
        </DetailsFieldsSection>
    )
}
